import { useEffect } from 'react';
import { Button, Text, Title } from '@tremor/react';
import Modal from './Modal';

interface Props {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Style the confirm button as destructive (red) — default true since
   * delete is the dominant use case. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * In-app replacement for window.confirm(). Renders a focus-trapped, centered
 * dialog via the shared Modal (Escape / backdrop cancel, focus restore, scroll
 * lock, aria-modal); Enter confirms. Use whenever a click would lose data or
 * kick off an irreversible action.
 */
export default function ConfirmDialog({
  title, message, confirmLabel, cancelLabel, destructive = true,
  onConfirm, onCancel,
}: Props) {
  // Escape/backdrop cancellation + focus management come from Modal. Enter to
  // confirm is a ConfirmDialog-specific convenience, kept here.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm]);

  return (
    <Modal onClose={onCancel} ariaLabel={title} role="alertdialog" size="md" panelClassName="p-5">
      <Title>{title}</Title>
      <Text className="mt-2">{message}</Text>
      <div className="mt-4 flex gap-2 justify-end">
        <Button variant="light" onClick={onCancel}>{cancelLabel}</Button>
        <Button color={destructive ? 'red' : undefined} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
